import React, { FC, useCallback, useState } from "react";
import classNames, { Argument } from "classnames/dedupe";
import CSSTransition from "react-transition-group/CSSTransition";

import {
  Divider,
  HeaderNavSection,
  Link,
  MainHeader,
  MainHeaderContainer,
  NavItem,
  NavList,
  NavSwitcher,
  SearchContext,
  USPBanner,
} from "../../components";
import { SvgIcon } from "../../components/Icons";
import { MainLogo } from "../../icons";
import DMBrandsFlyout from "./FlyoutMenus/DMBrandsFlyout";
import DMClothingFlyout from "./FlyoutMenus/DMClothingFlyout";
import DMShoesFlyout from "./FlyoutMenus/DMShoesFlyout";
import DMSportsFlyout from "./FlyoutMenus/DMSportsFlyout";
import useScrollDir from "../../hooks/useScrollDir";
import { useSearchState } from "../../components/Navigational/Search/hooks/useSearchState";
import {
  mockEmptyServerResponse,
  mockServerResponse,
} from "../../mock/searchData";
import useWindowDimensions from "../../hooks/useWindowDimensions";
import { GRID_BREAKPOINTS } from "../../components/Fundamentals/Grid/Column";
import HeaderSearch from "../../components/Navigational/Search/HeaderSearch/HeaderSearch";
import { MenuIcons } from "./MenuIcons";

const navItems = {
  women: "women",
  men: "men",
  kids: "kids",
};

const DesktopMenuExtended: FC<{
  classes?: { banner?: Argument; menu: Argument };
  genderDriven?: boolean;
}> = ({ classes, genderDriven = true }) => {
  const MENU_HEIGHT = 52;
  const dir = useScrollDir();

  const [mockResponse, setMockResponse] = useState(mockEmptyServerResponse);

  const handleSearch = useCallback((searchTerm: string) => {
    if (searchTerm) setMockResponse(mockServerResponse);
    else setMockResponse(mockEmptyServerResponse);
  }, []);
  const context = useSearchState({
    onSearch: handleSearch,
  });

  const viewportSizes = useWindowDimensions();
  const isMobileSearch = viewportSizes.viewportWidth < GRID_BREAKPOINTS.lgm;

  const SECOND_LINE_MENU_HEIGHT = isMobileSearch ? 0 : 48;

  const onHeaderClear = useCallback(() => {
    setMockResponse(mockEmptyServerResponse);
  }, []);

  return (
    <SearchContext.Provider value={context}>
      <USPBanner
        shippingLabel="1–5 working Days"
        returnLabel="FREE Returns & Exchanges"
        pocketLabel="save 2.5% on all purchases"
      />

      <div
        className={classNames(
          "bg-color-white position-sticky container-fluid header-padding-placeholder-main",
          classes?.menu,
        )}
        style={
          !context.isActive
            ? { top: dir === "down" ? `-${MENU_HEIGHT}px` : 0 }
            : {}
        }
        id="main-menu-header"
      >
        <MainHeader isGender={genderDriven}>
          <MainHeaderContainer variant="center">
            <Link
              href="/"
              classes={{
                root: classNames("header-brand", {
                  "d-xl-block": context.isActive,
                  "d-none": !isMobileSearch || context.isActive,
                  "d-lgm-block": !context.isActive,
                }),
              }}
            >
              <>
                <SvgIcon
                  icon={MainLogo}
                  isIcon={false}
                  width={155}
                  height={16}
                  classes={{ root: "header-brand__desktop" }}
                />
                <SvgIcon
                  icon={MainLogo}
                  isIcon={false}
                  width={116}
                  height={13}
                  classes={{ root: "header-brand__mobile" }}
                />
              </>
            </Link>

            <HeaderNavSection
              classes={{ root: !isMobileSearch ? "flex-grow-1" : "" }}
              variant={context.isActive ? "searchFocused" : "search"}
            >
              <HeaderSearch
                searchResults={mockResponse}
                onSearch={handleSearch}
                onClear={onHeaderClear}
                withDetails
                minSearchTermLength={0}
                menuHeight={MENU_HEIGHT}
                menuID="main-menu-header"
                opened={context.isActive}
                classes={{
                  dropdownListRoot: {
                    "mt-11": !isMobileSearch && genderDriven,
                  },
                  dropdownListContent: {
                    "pt-4": !genderDriven,
                  },
                }}
              />
            </HeaderNavSection>

            <CSSTransition
              appear
              classNames="uds-appear-fade"
              in={!context.isActive}
              timeout={300}
              mountOnEnter
              unmountOnExit={false}
              classes={{
                root: classNames(`header-icons-base`, {
                  "fade-out d-none d-xl-block": context.isActive,
                }),
              }}
            >
              <HeaderNavSection variant="icons">
                <MenuIcons />
              </HeaderNavSection>
            </CSSTransition>
          </MainHeaderContainer>
          {/** Menu */}
          <MainHeaderContainer variant="bottom">
            <HeaderNavSection variant="list">
              <NavSwitcher
                ariaLabel="Gender Menu"
                navItems={navItems}
                activeItem="women"
              />
              <NavList ariaLabel="Add menu">
                <NavItem
                  classes={{ root: "mobile-holder", wrapper: "mt-6 pt-3" }}
                  text="Clothing"
                  type="light"
                >
                  <DMClothingFlyout />
                </NavItem>
                <NavItem
                  classes={{ root: "mobile-holder", wrapper: "mt-6 pt-3" }}
                  text="Shoes"
                  type="light"
                >
                  <DMShoesFlyout />
                </NavItem>
                <NavItem
                  text="Sports"
                  type="light"
                  classes={{ wrapper: "mt-6 pt-3" }}
                >
                  <DMSportsFlyout />
                </NavItem>
                <NavItem
                  text="Brands"
                  type="light"
                  classes={{ wrapper: "mt-6 pt-3" }}
                >
                  <DMBrandsFlyout />
                </NavItem>
              </NavList>
            </HeaderNavSection>
          </MainHeaderContainer>
        </MainHeader>
      </div>
      <div
        className="header-padding-placeholder-divider container-fluid bg-color-transparent position-sticky"
        style={{
          top:
            dir === "down"
              ? `${SECOND_LINE_MENU_HEIGHT}px`
              : `${SECOND_LINE_MENU_HEIGHT + MENU_HEIGHT}px`,
        }}
      >
        <Divider size="bold" />
      </div>
    </SearchContext.Provider>
  );
};

export default DesktopMenuExtended;
